import { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Star, Check, Circle, Tag, Crosshair, ThumbsUp, ThumbsDown, Zap, MoreHorizontal, FolderKanban } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { usePrivacy } from '../hooks/usePrivacy'
import { useSettings } from '../hooks/useSettings'
import { formatTime, getUserTimezone } from '../lib/timezone'
import { formatOCC } from '../lib/occParser'
import PrivateValue from './PrivateValue'
import { buildPositionWorkspaceTarget } from '../lib/positionWorkspaceRoute'

// ── Formatters (shared with TradeLog) ────────────────────────────────────────

function fmtCurrency(val) {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(val) {
  if (val == null) return '—'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
}

function fmtDuration(mins) {
  if (mins == null) return '—'
  if (mins < 60) return `${mins}m`
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`
  const days = Math.floor(mins / 1440)
  const hours = Math.floor((mins % 1440) / 60)
  return `${days}d ${hours}h`
}

// Use centralized formatOCC from occParser.js

function fmtLongDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d)) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// formatLocalTime replaced by formatTime() from src/lib/timezone.js

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

function typeBadgeLabel(type) {
  if (type === 'etf') return 'ETF'
  if (!type) return '—'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

// ── Market Condition Options ─────────────────────────────────────────────────

const MARKET_CONDITIONS = [
  { value: '', label: 'Select...' },
  { value: 'trending_up', label: 'Trending Up' },
  { value: 'trending_down', label: 'Trending Down' },
  { value: 'range_bound', label: 'Range Bound' },
  { value: 'choppy', label: 'Choppy' },
  { value: 'news_driven', label: 'News Driven' },
  { value: 'gap_up', label: 'Gap Up' },
  { value: 'gap_down', label: 'Gap Down' },
]

// ── Star Rating ──────────────────────────────────────────────────────────────

function StarRating({ value, onChange }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} onClick={() => onChange(n === value ? null : n)} className="p-0.5 transition-colors">
          <Star
            size={18}
            className={n <= (value || 0) ? 'text-forest' : 'text-border'}
            fill={n <= (value || 0) ? 'rgb(var(--color-forest))' : 'none'}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  )
}

// ── Tag Placeholder ──────────────────────────────────────────────────────────

function TagSection({ icon: Icon, label }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-inkMuted" strokeWidth={1.5} />
        <span className="font-mono text-xs text-inkMuted">{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-[10px] px-2.5 py-1 rounded-full bg-canvas2 text-inkMuted border border-border">
          Select tag
        </span>
        <button className="p-1 text-inkMuted hover:text-ink transition-colors">
          <MoreHorizontal size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Saved Indicator ──────────────────────────────────────────────────────────

function SavedIndicator({ show }) {
  return (
    <span
      className={`font-mono text-[10px] text-forest transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`}
    >
      Saved
    </span>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function TradeDetailContent({ tradeId, nicknameMap, projectId }) {
  const { privacyMode } = usePrivacy()
  const { settings } = useSettings()
  const tz = getUserTimezone(settings)

  const [trade, setTrade] = useState(null)
  const [executions, setExecutions] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('data')
  const [savedField, setSavedField] = useState(null)

  // Editable field state
  const [stopLoss, setStopLoss] = useState('')
  const [takeProfit, setTakeProfit] = useState('')
  const [quality, setQuality] = useState(null)
  const [followedPlan, setFollowedPlan] = useState(null)
  const [marketCondition, setMarketCondition] = useState('')
  const [notes, setNotes] = useState('')

  const notesTimer = useRef(null)

  // Load trade + executions
  useEffect(() => {
    if (!tradeId) return
    if (!trade) setLoading(true)
    async function load() {
      const [{ data: t }, { data: execs }] = await Promise.all([
        supabase.from('trades').select('*').eq('id', tradeId).single(),
        supabase.from('executions').select('*').eq('trade_id', tradeId).order('executed_at', { ascending: true }),
      ])
      setTrade(t)
      setExecutions(execs || [])
      if (t) {
        setStopLoss(t.stop_loss != null ? String(t.stop_loss) : '')
        setTakeProfit(t.take_profit != null ? String(t.take_profit) : '')
        setQuality(t.trade_quality || null)
        setFollowedPlan(t.followed_plan)
        setMarketCondition(t.market_condition || '')
        setNotes(t.notes || '')
      }
      setLoading(false)
    }
    load()
  }, [tradeId])

  // Save helper
  const saveField = useCallback(async (field, value) => {
    if (!trade) return
    const updates = { [field]: value, updated_at: new Date().toISOString() }

    if (field === 'stop_loss' && value != null && value !== '') {
      const sl = Number(value)
      const multiplier = trade.asset_type === 'option' ? 100 : 1

      // Set initial_stop if this is the first stop for this trade
      if (!trade.initial_stop) {
        updates.initial_stop = sl
      }

      // Use initial_stop for risk calculation (original risk), fall back to current stop
      const riskStop = trade.initial_stop || sl
      const riskAmount = Math.abs(Number(trade.avg_entry_price) - riskStop) * Number(trade.quantity_total) * multiplier
      updates.risk_amount = riskAmount
      if (trade.net_pnl != null && riskAmount > 0) {
        updates.r_multiple = Number(trade.net_pnl) / riskAmount
      }
    }

    await supabase.from('trades').update(updates).eq('id', trade.id)

    // Update local trade state with saved values
    setTrade(prev => prev ? { ...prev, ...updates } : prev)

    setSavedField(field)
    setTimeout(() => setSavedField(null), 1500)
  }, [trade])

  // Debounced notes save
  function handleNotesChange(val) {
    setNotes(val)
    clearTimeout(notesTimer.current)
    notesTimer.current = setTimeout(() => saveField('notes', val), 1000)
  }

  // Mark as Reviewed
  async function toggleReviewed() {
    if (!trade) return
    const next = trade.review_status === 'reviewed' ? 'pending' : 'reviewed'
    await supabase.from('trades').update({ review_status: next, updated_at: new Date().toISOString() }).eq('id', trade.id)
    setTrade(prev => prev ? { ...prev, review_status: next } : prev)
  }

  if (loading) {
    return <div className="p-6 font-mono text-xs text-inkMuted animate-pulse">Loading trade...</div>
  }

  if (!trade) {
    return <div className="p-6 font-mono text-xs text-inkMuted">Trade not found</div>
  }

  const pnl = Number(trade.net_pnl)
  const isWin = pnl > 0
  const isLoss = pnl < 0
  const isOption = trade.asset_type === 'option'
  const displaySymbol = isOption ? formatOCC(trade.symbol) : (trade.symbol || '—')
  const acctName = nicknameMap?.[trade.account_number] || ('···' + (trade.account_number || '').slice(-4))
  const totalFees = Number(trade.commission || 0) + Number(trade.fees || 0)

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="px-5 pt-5 pb-4 border-b border-border space-y-3">
        {/* Symbol + badges */}
        <div className="flex items-start gap-2 flex-wrap">
          <span className="font-black text-lg tracking-wide">{displaySymbol}</span>
          <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-canvas2 text-inkMuted border border-border">
            {typeBadgeLabel(trade.asset_type)}
          </span>
          <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full font-semibold ${
            trade.side === 'long' ? 'bg-forest text-white' : 'bg-ember text-white'
          }`}>
            {trade.side?.toUpperCase() || '—'}
          </span>
          {trade.net_pnl != null && (
            <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full font-semibold ${
              isWin ? 'bg-forest text-white' : isLoss ? 'bg-ember text-white' : 'bg-canvas2 text-inkMuted border border-border'
            }`}>
              {isWin ? 'WIN' : isLoss ? 'LOSS' : 'BE'}
            </span>
          )}
          {projectId && (
            <Link
              to={buildPositionWorkspaceTarget({ symbol: trade.symbol, projectId })}
              className="font-mono text-[10px] px-2 py-0.5 rounded-full border border-border text-inkMuted hover:text-forest hover:border-forest/30 transition-colors inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <FolderKanban size={10} />
              Position Project
            </Link>
          )}
        </div>

        {/* P&L */}
        <div className="flex items-baseline gap-3">
          <span className={`font-mono text-2xl font-bold ${clr(pnl)}`}>
            <PrivateValue>{trade.net_pnl != null ? (pnl >= 0 ? '+' : '') + fmtCurrency(pnl) : '—'}</PrivateValue>
          </span>
          <span className={`font-mono text-sm ${clr(Number(trade.pnl_percent))}`}>
            <PrivateValue>{fmtPct(trade.pnl_percent)}</PrivateValue>
          </span>
        </div>

        {/* Account + dates + review */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="space-y-1">
            <div className="font-mono text-xs text-inkMuted">{acctName}</div>
            <div className="font-mono text-[11px] text-inkMuted">
              {fmtLongDate(trade.entry_date)}
              {trade.exit_date ? ` \u2192 ${fmtLongDate(trade.exit_date)}` : ' \u2192 Open'}
              {trade.duration_minutes != null && (
                <span className="ml-2 text-inkMuted">({fmtDuration(trade.duration_minutes)})</span>
              )}
            </div>
          </div>
          <button
            onClick={toggleReviewed}
            className={`flex items-center gap-1.5 font-mono text-[10px] px-3 py-1.5 rounded-full border transition-colors ${
              trade.review_status === 'reviewed'
                ? 'bg-forest text-white border-forest'
                : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
            }`}
          >
            {trade.review_status === 'reviewed'
              ? <><Check size={11} /> Reviewed</>
              : <><Circle size={11} /> Mark Reviewed</>
            }
          </button>
        </div>
      </div>

      {/* ── Tab Bar ───────────────────────────────────────────────────── */}
      <div className="flex border-b border-border px-5">
        {['data', 'executions'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-forest text-ink font-semibold'
                : 'border-transparent text-inkMuted hover:text-ink'
            }`}
          >
            {tab === 'data' ? 'Data' : `Executions (${executions.length})`}
          </button>
        ))}
      </div>

      {/* ── Tab Content ───────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'data' ? (
          <div className="px-5 py-4 space-y-5">
            {/* Trade Statistics */}
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Trade Statistics</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                <StatRow label="Entry Price" value={<PrivateValue>{fmtCurrency(trade.avg_entry_price)}</PrivateValue>} />
                <StatRow label="Exit Price" value={<PrivateValue>{fmtCurrency(trade.avg_exit_price)}</PrivateValue>} />
                <StatRow label="Quantity" value={trade.quantity_total != null ? (isOption ? Number(trade.quantity_total).toFixed(1) : Math.round(Number(trade.quantity_total))) : '—'} />
                <StatRow label="Gross P&L" value={<PrivateValue><span className={clr(Number(trade.gross_pnl))}>{fmtCurrency(trade.gross_pnl)}</span></PrivateValue>} />
                <StatRow label="Net P&L" value={<PrivateValue><span className={clr(pnl)}>{fmtCurrency(trade.net_pnl)}</span></PrivateValue>} />
                <StatRow label="Net ROI" value={<PrivateValue><span className={clr(Number(trade.pnl_percent))}>{fmtPct(trade.pnl_percent)}</span></PrivateValue>} />
                <StatRow label="Commission" value={<PrivateValue>{fmtCurrency(trade.commission)}</PrivateValue>} />
                <StatRow label="Fees" value={<PrivateValue>{fmtCurrency(trade.fees)}</PrivateValue>} />
                <StatRow label="Duration" value={fmtDuration(trade.duration_minutes)} />
                <StatRow label="Asset Type" value={typeBadgeLabel(trade.asset_type)} />
                <StatRow label="Import Source" value={trade.import_source || '—'} />
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Editable Fields */}
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-3">Trade Details</div>
              <div className="space-y-3">
                {/* Stop Loss */}
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs text-inkMuted">Stop Loss</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={stopLoss}
                      onChange={e => setStopLoss(e.target.value)}
                      onBlur={() => saveField('stop_loss', stopLoss ? Number(stopLoss) : null)}
                      className="font-mono text-xs pl-2 pr-3 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[100px] text-right"
                      placeholder="—"
                    />
                    <SavedIndicator show={savedField === 'stop_loss'} />
                  </div>
                </div>

                {/* Risk Amount (computed, read-only) */}
                {trade.risk_amount != null && (
                  <div className="flex items-center justify-between">
                    <label className="font-mono text-xs text-inkMuted">Risk $</label>
                    <span className="font-mono text-xs text-ember"><PrivateValue>{fmtCurrency(trade.risk_amount)}</PrivateValue></span>
                  </div>
                )}

                {/* R-Multiple (computed, read-only) */}
                {trade.r_multiple != null && (
                  <div className="flex items-center justify-between">
                    <label className="font-mono text-xs text-inkMuted">R-Multiple</label>
                    <span className={`font-mono text-xs ${clr(Number(trade.r_multiple))}`}>{Number(trade.r_multiple).toFixed(2)}</span>
                  </div>
                )}

                {/* Take Profit */}
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs text-inkMuted">Take Profit</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={takeProfit}
                      onChange={e => setTakeProfit(e.target.value)}
                      onBlur={() => saveField('take_profit', takeProfit ? Number(takeProfit) : null)}
                      className="font-mono text-xs pl-2 pr-3 py-1 rounded-lg bg-canvas2 border border-border text-ink w-[100px] text-right"
                      placeholder="—"
                    />
                    <SavedIndicator show={savedField === 'take_profit'} />
                  </div>
                </div>

                {/* Trade Quality */}
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs text-inkMuted">Trade Quality</label>
                  <div className="flex items-center gap-2">
                    <StarRating value={quality} onChange={v => { setQuality(v); saveField('trade_quality', v) }} />
                    <SavedIndicator show={savedField === 'trade_quality'} />
                  </div>
                </div>

                {/* Followed Plan */}
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs text-inkMuted">Followed Plan</label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { const v = followedPlan === true ? null : true; setFollowedPlan(v); saveField('followed_plan', v) }}
                        className={`font-mono text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                          followedPlan === true ? 'bg-forest text-white border-forest' : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
                        }`}
                      >Yes</button>
                      <button
                        onClick={() => { const v = followedPlan === false ? null : false; setFollowedPlan(v); saveField('followed_plan', v) }}
                        className={`font-mono text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                          followedPlan === false ? 'bg-ember text-white border-ember' : 'bg-canvas2 text-inkMuted border-border hover:text-ink'
                        }`}
                      >No</button>
                    </div>
                    <SavedIndicator show={savedField === 'followed_plan'} />
                  </div>
                </div>

                {/* Market Condition */}
                <div className="flex items-center justify-between">
                  <label className="font-mono text-xs text-inkMuted">Market Condition</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={marketCondition}
                      onChange={e => { setMarketCondition(e.target.value); saveField('market_condition', e.target.value || null) }}
                      className="font-mono text-xs px-2 py-1 rounded-lg bg-canvas2 border border-border text-ink"
                    >
                      {MARKET_CONDITIONS.map(mc => (
                        <option key={mc.value} value={mc.value}>{mc.label}</option>
                      ))}
                    </select>
                    <SavedIndicator show={savedField === 'market_condition'} />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="font-mono text-xs text-inkMuted">Notes</label>
                    <SavedIndicator show={savedField === 'notes'} />
                  </div>
                  <textarea
                    value={notes}
                    onChange={e => handleNotesChange(e.target.value)}
                    onBlur={() => { clearTimeout(notesTimer.current); saveField('notes', notes) }}
                    rows={4}
                    className="w-full font-mono text-xs px-3 py-2 rounded-lg bg-canvas2 border border-border text-ink resize-none"
                    placeholder="Add trade notes..."
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border" />

            {/* Tag System Placeholders */}
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Tags</div>
              <div className="divide-y divide-border">
                <TagSection icon={Crosshair} label="Trade Type" />
                <TagSection icon={ThumbsUp} label="Good Habits" />
                <TagSection icon={ThumbsDown} label="Bad Habits" />
                <TagSection icon={Zap} label="Setups" />
                <TagSection icon={Tag} label="Directional Response" />
              </div>
            </div>
          </div>
        ) : (
          /* ── Executions Tab ──────────────────────────────────────────── */
          <div className="px-5 py-4">
            {executions.length === 0 ? (
              <div className="text-center py-8">
                <div className="font-mono text-xs text-inkMuted">
                  No execution data available — this trade was created from position data.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Timeline */}
                <div className="space-y-0">
                  {executions.map((exec, i) => {
                    const isEntry = exec.execution_type === 'entry'
                    const isExit = exec.execution_type === 'exit' || exec.execution_type === 'partial_exit'
                    const colorClass = isEntry ? 'text-forest' : isExit ? 'text-ember' : 'text-inkMuted'
                    const dotBg = isEntry ? 'bg-forest' : isExit ? 'bg-ember' : 'bg-canvas2'

                    // Running position
                    let runningQty = 0
                    for (let j = 0; j <= i; j++) {
                      const e = executions[j]
                      if (e.execution_type === 'entry') runningQty += Number(e.quantity || 0)
                      else runningQty -= Number(e.quantity || 0)
                    }

                    const tsStr = exec.executed_at ? formatTime(exec.executed_at, tz) : '—'

                    return (
                      <div key={exec.id} className="flex gap-3">
                        {/* Timeline dot + line */}
                        <div className="flex flex-col items-center">
                          <div className={`w-2.5 h-2.5 rounded-full ${dotBg} mt-1.5 shrink-0`} />
                          {i < executions.length - 1 && <div className="w-px flex-1 bg-border" />}
                        </div>
                        {/* Content */}
                        <div className="pb-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-mono text-[10px] font-semibold uppercase ${colorClass}`}>
                              {exec.execution_type?.replace('_', ' ') || '—'}
                            </span>
                            <span className="font-mono text-[10px] text-inkMuted">{exec.side?.toUpperCase()}</span>
                            <span className="font-mono text-[11px] text-inkMuted">{tsStr}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="font-mono text-xs">
                              {exec.quantity != null ? (isOption ? Number(exec.quantity).toFixed(1) : Math.round(Number(exec.quantity))) : '—'} @ <PrivateValue>{fmtCurrency(exec.price)}</PrivateValue>
                            </span>
                            {(exec.commission || exec.fees) && (
                              <span className="font-mono text-[10px] text-inkMuted">
                                fees: <PrivateValue>{fmtCurrency(Number(exec.commission || 0) + Number(exec.fees || 0))}</PrivateValue>
                              </span>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-inkMuted mt-0.5">
                            Position: {Math.abs(runningQty)} {runningQty === 0 ? '(flat)' : ''}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Weighted averages */}
                <div className="border-t border-border pt-3">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Computed Averages</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    <StatRow label="Avg Entry" value={<PrivateValue>{fmtCurrency(trade.avg_entry_price)}</PrivateValue>} />
                    <StatRow label="Avg Exit" value={<PrivateValue>{fmtCurrency(trade.avg_exit_price)}</PrivateValue>} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Stat Row helper ──────────────────────────────────────────────────────────

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="font-mono text-[11px] text-inkMuted">{label}</span>
      <span className="font-mono text-xs text-ink">{value}</span>
    </div>
  )
}
